import AIChatWidget from '@/components/aichat/AiChatWidget';
import SeoHead from '@/components/seo/SeoHead';
import { Button } from '@/components/ui/button';
import LandingLayout from '@/layouts/landing-layout';
import { Link } from '@inertiajs/react';
import { ArrowRight, Brain, ChevronRight, Sparkles } from 'lucide-react';

const baseUrl = import.meta.env.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

const programSections = [
    {
        title: 'College Degree Programs',
        items: [
            'Juris Doctor',
            'Bachelor of Science in Geodetic Engineering (BSGE) •',
            'Bachelor of Science in Accountancy (BSA)',
            'Bachelor of Elementary Education (BEED) ****',
            'Bachelor of Secondary Education (BSED) ****',
            'Bachelor of Science in Criminology (BSCRIM) •',
            'Bachelor of Science in Nursing (BSN) •',
            'Bachelor of Arts in Political Science (AB PolSci) ****',
            'Bachelor of Arts in English Language Studies (AB English) ****',
            'Bachelor of Arts in Communication (AB Comm) ****',
            'Bachelor of Science in Business Administration (BSBA) ****',
            'Bachelor of Science in Management Accounting (BSMA)',
            'Bachelor of Science in Hospitality Management (BSHM) ***',
            'Bachelor of Science in Information Technology (BSIT) ***',
        ],
        nested: {
            'Bachelor of Secondary Education (BSED) ****': ['English', 'Filipino', 'Mathematics', 'Science'],
            'Bachelor of Science in Business Administration (BSBA) ****': [
                'Financial Management',
                'Marketing Management',
                'Human Resource Management',
            ],
        } as Record<string, string[]>,
    },
    {
        title: 'Postgraduate Courses',
        items: [
            'Doctor of Philosophy (Ph.D.) **',
            'Doctor of Education (Ed.D.) **',
            'Master of Arts in Education (MAEd) **',
            'Master of Arts in Education - Language and Literature (MAEd-LL) **',
            'Master in Public Administration (MPA) **',
            'Master in Business Administration (MBA - Thesis & Non-Thesis) **',
        ],
        nested: {} as Record<string, string[]>,
    },
    {
        title: 'Basic Education Center',
        items: [
            'Elementary School (Nursery / Kindergarten to Grade 6)',
            'Junior High School (Grade 7 to Grade 10) ***',
            'Senior High School (Grade 11 to Grade 12) *',
        ],
        nested: {
            'Senior High School (Grade 11 to Grade 12) *': [
                'Academic Track: Accountancy, Business and Management (ABM)',
                'Academic Track: Science, Technology, Engineering and Mathematics (STEM)',
                'Academic Track: Humanities and Social Sciences (HUMSS)',
                'Technical-Vocational Track: Home Economics (HE)',
                'Technical-Vocational Track: Information and Communications Technology (ICT)',
            ],
        } as Record<string, string[]>,
    },
];

const accreditationStyles: Record<string, string> = {
    '•': 'bg-[#0A7A35]/15 text-[#0A7A35]',
    '*': 'bg-primary/15 text-primary',
    '**': 'bg-primary/15 text-primary',
    '***': 'bg-emerald-500/15 text-emerald-600',
    '****': 'bg-amber-500/15 text-amber-600',
};

function parseProgramLabel(item: string): { name: string; accreditation: string | null } {
    const match = item.match(/\s(•|\*{1,4})$/);

    if (!match) {
        return { name: item, accreditation: null };
    }

    return {
        name: item.slice(0, -match[1].length).trim(),
        accreditation: match[1],
    };
}

export default function LandingCourses() {
    return (
        <LandingLayout>
            <SeoHead
                title="Course Offerings"
                description="Explore Northeastern College programs, from basic education to postgraduate courses, including accredited degree offerings."
                image={`${baseUrl}/images/logo.webp`}
            />

            <section className="bg-muted pb-16">
                <div className="relative min-h-[380px] overflow-hidden bg-black md:min-h-[460px]">
                    <img
                        src="/images/hero/course.png"
                        alt="Northeastern College campus"
                        className="absolute inset-0 h-full w-full scale-105 object-cover opacity-60"
                    />

                    <div className="absolute inset-0 bg-gradient-to-r from-[#02301c]/95 via-[#02301c]/80 to-transparent" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(10,122,53,0.35),transparent_60%)]" />
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
                    <div className="pointer-events-none absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-emerald-500/10 blur-[100px]" />
                    <div className="pointer-events-none absolute right-1/4 bottom-1/4 h-64 w-64 rounded-full bg-yellow-500/5 blur-[100px]" />

                    <div className="relative z-10 mx-auto flex min-h-[380px] max-w-7xl flex-col justify-center px-4 py-16 text-white md:min-h-[460px] md:py-24">
                        <div className="mb-6 flex items-center gap-2 text-xs font-semibold tracking-wide text-[#21d36b]/90 uppercase md:text-sm">
                            <Link href="/" prefetch className="transition-colors hover:text-emerald-300">
                                Home
                            </Link>
                            <ChevronRight className="size-4" aria-hidden="true" />
                            <span className="text-white/80">Admissions</span>
                        </div>

                        <h1 className="max-w-4xl text-4xl leading-tight font-extrabold tracking-tight text-white md:text-6xl">
                            Academic Course Offerings
                        </h1>
                        <p className="mt-6 max-w-3xl text-base leading-relaxed font-normal text-white/85 md:text-lg">
                            Explore our comprehensive degree programs, postgraduate courses, and basic education options.
                        </p>
                    </div>
                </div>

                <div className="relative z-20 mx-auto -mt-8 max-w-7xl px-4">
                    <div className="space-y-6">
                        {programSections.map((section, sectionIndex) => (
                            <details
                                key={section.title}
                                className="group border-border bg-card overflow-hidden rounded-2xl border shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md"
                                open={sectionIndex === 0}
                            >
                                <summary className="flex cursor-pointer list-none items-center justify-between px-6 py-5 select-none focus:outline-none">
                                    <span className="text-foreground text-base font-bold md:text-lg">{section.title}</span>
                                    <span className="bg-muted text-muted-foreground border-border ml-4 flex h-8 w-8 items-center justify-center rounded-full border transition-transform duration-200 group-open:rotate-180">
                                        <svg viewBox="0 0 20 20" className="h-4 w-4" aria-hidden="true">
                                            <path
                                                d="M5 7.5L10 12.5L15 7.5"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </span>
                                </summary>

                                <div className="border-border bg-card border-t px-6 pb-6">
                                    <ul className="text-muted-foreground space-y-3 pt-5 pl-2 text-sm leading-relaxed md:text-base">
                                        {section.items.map((item) => {
                                            const program = parseProgramLabel(item);

                                            return (
                                                <li key={item} className="flex flex-col gap-1">
                                                    <div className="flex items-start gap-2.5">
                                                        <span className="mt-2.5 h-2 w-2 shrink-0 rounded-full bg-[#0A7A35]" />
                                                        <span className="flex flex-wrap items-center gap-2">
                                                            <span className="text-foreground font-medium">{program.name}</span>
                                                            {program.accreditation && (
                                                                <span
                                                                    className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-xs font-bold ${
                                                                        accreditationStyles[program.accreditation]
                                                                    }`}
                                                                    aria-label={`Accreditation marker ${program.accreditation}`}
                                                                >
                                                                    {program.accreditation}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                    {section.nested[item] && (
                                                        <ul className="text-muted-foreground mt-2 ml-7 space-y-2 border-l border-emerald-500/20 pl-4 text-sm">
                                                            {section.nested[item].map((nestedItem) => (
                                                                <li key={nestedItem} className="flex items-center gap-2">
                                                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500/60" />
                                                                    <span>{nestedItem}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </details>
                        ))}

                        <div className="bg-card border-border relative overflow-hidden rounded-2xl border shadow-sm">
                            <div className="absolute top-0 left-0 h-1 w-full bg-[#0A7A35]" />
                            <div className="px-6 py-6">
                                <h5 className="text-foreground mb-4 flex items-center gap-2 text-base font-bold">
                                    <Sparkles className="size-4 text-[#0A7A35]" />
                                    Program Accreditation Legend
                                </h5>
                                <div className="text-muted-foreground grid gap-4 text-xs sm:grid-cols-2 md:grid-cols-3 md:text-sm">
                                    <div className="bg-muted/40 border-border/40 flex items-center gap-2.5 rounded-xl border p-2.5">
                                        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0A7A35]/15 text-sm font-bold text-[#0A7A35]">
                                            •
                                        </span>
                                        <span className="font-medium">Associate Status</span>
                                    </div>
                                    <div className="bg-muted/40 border-border/40 flex items-center gap-2.5 rounded-xl border p-2.5">
                                        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-bold text-primary">
                                            *
                                        </span>
                                        <span className="font-medium">Level I Accredited</span>
                                    </div>
                                    <div className="bg-muted/40 border-border/40 flex items-center gap-2.5 rounded-xl border p-2.5">
                                        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-bold text-primary">
                                            **
                                        </span>
                                        <span className="font-medium">Level II - 1st Re-Accredited</span>
                                    </div>
                                    <div className="bg-muted/40 border-border/40 flex items-center gap-2.5 rounded-xl border p-2.5">
                                        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-sm font-bold text-emerald-600">
                                            ***
                                        </span>
                                        <span className="font-medium">Level II - 2nd Re-Accredited</span>
                                    </div>
                                    <div className="bg-muted/40 border-border/40 flex items-center gap-2.5 rounded-xl border p-2.5">
                                        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-sm font-bold text-amber-600">
                                            ****
                                        </span>
                                        <span className="font-medium">Level III Re-Accredited</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 text-center">
                            <p className="text-xs leading-relaxed font-bold tracking-wider text-[#0A7A35]/80 uppercase">
                                Accredited by the Philippine Association of Colleges and Universities
                                <br />- Commission on Accreditation (PACU-COA)
                            </p>
                        </div>

                        <div className="mt-8">
                            <div className="bg-card border-border group relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-8">
                                <div className="pointer-events-none absolute top-0 right-0 h-48 w-48 rounded-full bg-emerald-500/5 blur-3xl transition-colors group-hover:bg-emerald-500/10" />
                                <div className="absolute top-0 left-0 h-full w-1.5 bg-[#0A7A35]" />
                                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#0A7A35]/10 px-4.5 py-1.5 text-xs font-bold tracking-wider text-[#0A7A35] uppercase">
                                    <Sparkles className="h-3.5 w-3.5" />
                                    Course Finder
                                </div>
                                <h2 className="text-foreground text-xl font-bold">Not sure which course fits you?</h2>
                                <p className="text-muted-foreground mt-2 max-w-2xl text-sm leading-relaxed">
                                    Answer a short set of questions and get the top 3 course matches based on your goals, interests, and strengths.
                                </p>
                                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                    <Button asChild className="rounded-full bg-[#0A7A35] px-6 text-white hover:bg-[#08632b]">
                                        <Link href={route('courseFinder')}>
                                            Open Course Finder
                                            <ArrowRight className="ml-1 h-4 w-4" />
                                        </Link>
                                    </Button>
                                    <div className="border-border text-muted-foreground bg-muted/40 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium">
                                        <Brain className="h-4 w-4 text-[#0A7A35]" />8 guided questions
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
